import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ReturnStatus from 'enums/ReturnStatus'
import LinkButton from 'components/share/LinkButton'
import ErrorCode from 'enums/ErrorCode'
import { AutomationId } from 'utils/automationUtil'
import openHelpPage from 'utils/helpLinkUtil'
// @ts-ignore
import { QuestionMarkOutlined, Tooltip } from '@netbasequid/core'
// @ts-ignore
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { InsightChartGuardProps } from '../../types'

const InsightChartGuardContainer = styled.div<{
  mode: Theme
}>`
  margin: auto;
  text-align: center;
  color: ${({ mode }) => sys[mode].text.placeholder};
`

const QuestionMark = () => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  return (
    <Tooltip
      mode={theme}
      content={
        <div>
          {t('noInsightTooltip')}
          <br /> {t('clickHelpMoreInfo')}
        </div>
      }>
      <LinkButton data-testid={AutomationId.link.more} onClick={() => openHelpPage(8240170)}>
        <QuestionMarkOutlined fontSize={14} mode={theme} />
      </LinkButton>
    </Tooltip>
  )
}

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <span data-testid={AutomationId.error.message}>{children}</span>
)

const InsightChartGuard = ({ isVoc, data, children }: InsightChartGuardProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()

  if (!data) return null
  const { isSetChanged, isSetEmpty, status, error } = data
  if (error?.errorCode === ErrorCode.TOPIC_MANAGER_INDEX_NOT_READY_ERROR)
    return (
      <InsightChartGuardContainer mode={theme}>
        <ErrorMessage>{t('topicLoadingShort')}</ErrorMessage>
      </InsightChartGuardContainer>
    )
  if (status === ReturnStatus.LANGUAGE_NOT_SUPPORTED)
    return (
      <InsightChartGuardContainer mode={theme}>
        <ErrorMessage>{t('languageUnsupported')}</ErrorMessage>
        &nbsp;
        <LinkButton data-testid={AutomationId.link.more} onClick={() => openHelpPage(8187320)}>
          {t('moreThreeDot')}
        </LinkButton>
      </InsightChartGuardContainer>
    )
  if (isSetChanged && isSetEmpty) {
    return (
      <InsightChartGuardContainer mode={theme}>
        <ErrorMessage>{t('noInsightBecauseLowCount')}</ErrorMessage>
        &nbsp;
        <QuestionMark />
      </InsightChartGuardContainer>
    )
  }
  if (isSetEmpty) {
    return isVoc ? (
      <InsightChartGuardContainer mode={theme}>
        <ErrorMessage>{t('noVocData')}</ErrorMessage>
        &nbsp;
        <LinkButton data-testid={AutomationId.link.more} onClick={() => openHelpPage(8240236)}>
          {t('moreThreeDot')}
        </LinkButton>
      </InsightChartGuardContainer>
    ) : (
      <InsightChartGuardContainer mode={theme}>
        <ErrorMessage>{t('noData')}</ErrorMessage>
        &nbsp;
        <LinkButton data-testid={AutomationId.link.more} onClick={() => openHelpPage(8240236)}>
          {t('moreThreeDot')}
        </LinkButton>
      </InsightChartGuardContainer>
    )
  }
  return children
}

export default InsightChartGuard
